import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Modal,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import SearchIcon from "@mui/icons-material/Search";
import { debounce } from "lodash";
import { SortedDataType } from "../../../../../domain/models/bricks-working-report/sorted-data-type";

const style = {
  position: "absolute" as "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 400,
  bgcolor: "background.paper",
  border: "2px solid #000",
  boxShadow: 24,
  p: 4,
};

type ColumnFilterKey = "area" | "headquarter" | "brick";

type Props = {
  openFilter: boolean;
  filterKey: "area" | "headquarter" | "brick";
  title: string;
  uniqueList: (key: ColumnFilterKey) => string[];
  handleFilter: () => void;
  handleSelectedOptions: (selected: string[]) => void;
  selectedValues?: string[];
};

export const BrickAreaFilter: React.FC<Props> = ({
  openFilter,
  handleFilter,
  uniqueList,
  handleSelectedOptions,
  filterKey,
  title,
  selectedValues,
}) => {
  const [selectedOptions, setSelectedOptions] = useState<string[]>([]);
  const [searchedData, setSearchedData] = useState<string>("");
  const [filteredItems, setFilteredItems] = useState<any[]>([]);

  const allItems = React.useMemo(
    () => uniqueList(filterKey),
    [filterKey, uniqueList]
  );

  const handleChange = (value: string) => () => {
    setSelectedOptions((prevState) => {
      if (prevState?.includes(value)) {
        return prevState.filter((item) => item !== value);
      } else {
        return [...prevState, value];
      }
    });
  };
  const handleSelectAll = () => {
    if (selectedOptions.length === allItems.length) {
      setSelectedOptions([]);
    } else {
      setSelectedOptions(allItems);
    }
  };

  const handleSearchFilter = debounce(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const value = e.target.value.toLowerCase();
      setSearchedData(value);

      const filtered = uniqueList(filterKey).filter((item) =>
        item.toLowerCase().includes(value)
      );

      setFilteredItems(filtered);
    },
    300
  );

  const sortedItems = () => {
    return searchedData.length == 0
      ? [...uniqueList(filterKey)]
      : [...filteredItems];
  };
  if (searchedData !== "") {
    const searchedItemIndex = sortedItems().findIndex(
      (item) => item.toLowerCase() === searchedData
    );
    if (searchedItemIndex !== -1) {
      const searchedItem = sortedItems().splice(searchedItemIndex, 1);
      sortedItems().unshift(searchedItem[0]);
    }
  }

  useEffect(() => {
    setSelectedOptions(sortedItems());
  }, [searchedData]);

  useEffect(() => {
    if (!openFilter) return;
    if (!selectedValues || selectedValues.length === 0) {
      setSelectedOptions(allItems);
    } else {
      setSelectedOptions(selectedValues);
    }

    setSearchedData("");
    setFilteredItems([]);
  }, [openFilter, selectedValues, allItems]);

  const visibleItems =
    searchedData.length === 0
      ? allItems
      : allItems.filter((item) =>
          item.toLowerCase().includes(searchedData.toLowerCase())
        );

  const capitalizeFirstLetter = (text: string) =>
    text.charAt(0).toUpperCase() + text.slice(1);

  return (
    <Modal open={openFilter} onClose={() => handleFilter()}>
      <Box sx={style}>
        <Stack
          direction={"row"}
          justifyContent={"space-between"}
          alignItems={"center"}
          mb={2}
        >
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              letterSpacing: "0.3px",
            }}
          >
            Select {capitalizeFirstLetter(filterKey)}
          </Typography>

          <Stack direction={"row"} gap={2}>
            <Button
              variant="contained"
              sx={{
                bgcolor: "#0D71B7",
                "&:hover": {
                  bgcolor: "#0D71B7",
                },
              }}
              onClick={() => {
                handleSelectedOptions(selectedOptions);
              }}
            >
              Apply
            </Button>
            <Button
              onClick={() => {
                handleFilter();
              }}
              variant="contained"
              sx={{
                bgcolor: "#ef4242ff",
                "&:hover": {
                  bgcolor: "#ec3b3bff",
                },
              }}
            >
              Close
            </Button>
          </Stack>
        </Stack>
        <TextField
          fullWidth
          placeholder="Search"
          size="small"
          onChange={(e) => handleSearchFilter(e)}
          sx={{
            mb: 2,
            "& .MuiOutlinedInput-root": {
              borderRadius: "12px",
              paddingRight: "8px",
              "& fieldset": {
                borderColor: "#0D71B7",
                borderWidth: "2px",
              },
              "&:hover fieldset": {
                borderColor: "#0D71B7",
              },
              "&.Mui-focused fieldset": {
                borderColor: "#0D71B7",
              },
            },
          }}
          InputProps={{
            endAdornment: (
              <SearchIcon
                sx={{
                  color: "#000",
                  cursor: "pointer",
                }}
              />
            ),
          }}
        />

        <Box maxHeight={"60vh"} overflow={"scroll"}>
          {sortedItems().length !== 0 && (
            <FormControlLabel
              sx={{ mb: 1 }}
              control={
                <Checkbox
                  checked={selectedOptions.length === allItems.length}
                  onChange={handleSelectAll}
                />
              }
              label="Select All"
            />
          )}
          <FormGroup>
            {sortedItems().length == 0 ? (
              <Typography variant="h6" sx={{ fontSize: "15px" }}>
                No {filterKey} found !
              </Typography>
            ) : (
              sortedItems()?.map((row: any) => (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={selectedOptions?.includes(row)}
                      onChange={handleChange(row)}
                    />
                  }
                  label={row}
                />
              ))
            )}
          </FormGroup>
        </Box>
      </Box>
    </Modal>
  );
};

export default BrickAreaFilter;
